import { RequirementsComplianceScoreModel } from '@abb-procure/data';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Injector,
  input,
  LOCALE_ID,
  runInInjectionContext,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { Options } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { DecimalPipe, transformDecimal } from '../../pipes/decimal.pipe';

@Component({
  selector: 'procure-requirements-compliance-graph',
  templateUrl: './requirements-compliance-graph.component.html',
  styleUrl: './requirements-compliance-graph.component.scss',
  imports: [HighchartsChartComponent],
  providers: [DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RequirementsComplianceGraph {
  readonly scores = input<RequirementsComplianceScoreModel[] | null>();

  private readonly locale: string = inject(LOCALE_ID);
  private readonly injector = inject(Injector);

  protected readonly chartOptions = signal<Options>(this.buildInitialOptions());

  constructor() {
    afterNextRender(() => {
      runInInjectionContext(this.injector, () => {
        effect(() => {
          this.update(this.scores());
        });
      });
    });
  }

  private update(
    scores: RequirementsComplianceScoreModel[] | null | undefined,
  ): void {
    if (!scores) {
      return;
    }

    this.chartOptions.update((opts) => ({
      ...opts,
      xAxis: {
        categories: scores.map((score) => score.label ?? ''),
      },
      series: [
        {
          type: 'column',
          name: 'Fulfilled',
          data: scores.map((score) => score.fulfilledCount / score.totalCount),
        },
        {
          type: 'column',
          name: 'For Clarification',
          data: scores.map(
            (score) => score.forClarificationCount / score.totalCount,
          ),
        },
      ],
    }));
  }

  // eslint-disable-next-line max-lines-per-function
  private buildInitialOptions(): Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return {
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      chart: {
        type: 'column',
        styledMode: true,
      },
      title: {
        text: '',
      },
      xAxis: {
        title: {
          text: '',
        },
        categories: [],
        gridLineWidth: 1,
      },
      yAxis: {
        title: {
          text: '',
        },
        min: 0,
        max: 1,
        labels: {
          formatter: (a): string =>
            `${transformDecimal(self.locale, Number(a.value) * 100, 2)}%`,
        },
        tickPixelInterval: 20,
        gridLineWidth: 1,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
      },
      tooltip: {
        formatter(): string {
          return `
          <span class="highcharts-header">${this.key}</span><br />
          ${this.series.name}: ${transformDecimal(self.locale, Number(this.y) * 100, 2)}%
        `;
        },
      },
      series: [
        {
          type: 'column',
          data: [],
        },
        {
          type: 'column',
          data: [],
        },
        {
          type: 'column',
          data: [],
        },
      ],
    };
  }
}
